/**
 * Family Settings Page
 *
 * Comprehensive settings page for family members to manage their portal
 * preferences, notification settings, and profile information.
 */

import React, { useState } from 'react';
import {
  useFamilyAccessSettings,
  useFamilyMemberProfile,
  useUpdateFamilyAccessSettings,
  useUpdateFamilyMemberProfile,
} from '../hooks';
import { NotificationSettings } from '../components/NotificationSettings';
import { useAuth } from '@/core/hooks';
import type { FamilyMember, FamilyRelationship } from '@fuchsiahomecarecatalin/family-engagement';
import type { UUID } from '@fuchsiahomecarecatalin/core/browser';
import type { FamilyAccessSettings } from '../services';

export const FamilySettings: React.FC = () => {
  const { user } = useAuth();
  const familyMemberId = (user?.id ?? sessionStorage.getItem('familyMemberId')) as UUID | null;
  const { data: familyMember, isLoading, error } = useFamilyMemberProfile(familyMemberId);
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'access' | 'data'>('profile');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-12rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error || !familyMember) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-12rem)]">
        <div className="text-center space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Unable to load settings</h2>
          <p className="text-sm text-gray-600">
            {error instanceof Error ? error.message : 'Please try again later'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage your profile, notification preferences, and data export options
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Settings">
          <button
            onClick={() => setActiveTab('profile')}
            className={`${
              activeTab === 'profile'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors`}
          >
            Profile Information
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`${
              activeTab === 'notifications'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors`}
          >
            Notifications
          </button>
          <button
            onClick={() => setActiveTab('data')}
            className={`${
              activeTab === 'data'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors`}
          >
            Data & Privacy
          </button>
          <button
            onClick={() => setActiveTab('access')}
            className={`${
              activeTab === 'access'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors`}
          >
            Access
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'profile' && <ProfileTab familyMember={familyMember} />}
        {activeTab === 'notifications' && (
          <NotificationSettings
            familyMemberId={familyMember.id}
            currentPreferences={familyMember.notificationPreferences}
          />
        )}
        {activeTab === 'access' && <AccessTab familyMember={familyMember} />}
        {activeTab === 'data' && <DataTab familyMember={familyMember} />}
      </div>
    </div>
  );
};

/**
 * Profile Information Tab
 */
const ProfileTab: React.FC<{ familyMember: FamilyMember }> = ({ familyMember }) => {
  const [isEditing, setIsEditing] = useState(false);
  const updateProfile = useUpdateFamilyMemberProfile(familyMember.id);
  const [formData, setFormData] = useState({
    firstName: familyMember.firstName,
    lastName: familyMember.lastName,
    email: familyMember.email,
    phoneNumber: familyMember.phoneNumber,
    preferredContactMethod: familyMember.preferredContactMethod,
    relationship: familyMember.relationship,
    relationshipNote: familyMember.relationshipNote || '',
  });

  const handleSave = () => {
    updateProfile.mutate(formData, {
      onSuccess: () => setIsEditing(false),
    });
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Edit
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* Name */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              />
            ) : (
              <p className="text-sm text-gray-900">{familyMember.firstName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              />
            ) : (
              <p className="text-sm text-gray-900">{familyMember.lastName}</p>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          {isEditing ? (
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          ) : (
            <p className="text-sm text-gray-900">{familyMember.email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
          {isEditing ? (
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          ) : (
            <p className="text-sm text-gray-900">{familyMember.phoneNumber}</p>
          )}
        </div>

        {/* Preferred Contact Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Preferred Contact Method
          </label>
          {isEditing ? (
            <select
              value={formData.preferredContactMethod}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  preferredContactMethod: e.target.value as FamilyMember['preferredContactMethod'],
                })
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="EMAIL">Email</option>
              <option value="PHONE">Phone Call</option>
              <option value="SMS">Text Message (SMS)</option>
              <option value="PORTAL">Portal Only</option>
            </select>
          ) : (
            <p className="text-sm text-gray-900 capitalize">
              {familyMember.preferredContactMethod.replace('_', ' ').toLowerCase()}
            </p>
          )}
        </div>

        {/* Relationship */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Relationship to Care Recipient
          </label>
          {isEditing ? (
            <select
              value={formData.relationship}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  relationship: e.target.value as FamilyRelationship,
                })
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="SPOUSE">Spouse</option>
              <option value="PARENT">Parent</option>
              <option value="CHILD">Child</option>
              <option value="SIBLING">Sibling</option>
              <option value="GRANDPARENT">Grandparent</option>
              <option value="GRANDCHILD">Grandchild</option>
              <option value="GUARDIAN">Legal Guardian</option>
              <option value="POWER_OF_ATTORNEY">Power of Attorney</option>
              <option value="HEALTHCARE_PROXY">Healthcare Proxy</option>
              <option value="OTHER">Other</option>
            </select>
          ) : (
            <p className="text-sm text-gray-900 capitalize">
              {familyMember.relationship.replace(/_/g, ' ').toLowerCase()}
            </p>
          )}
        </div>

        {/* Relationship Note */}
        {(isEditing || familyMember.relationshipNote) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Relationship Details (Optional)
            </label>
            {isEditing ? (
              <textarea
                value={formData.relationshipNote}
                onChange={(e) => setFormData({ ...formData, relationshipNote: e.target.value })}
                rows={2}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., Primary caregiver, lives nearby"
              />
            ) : (
              <p className="text-sm text-gray-700">{familyMember.relationshipNote}</p>
            )}
          </div>
        )}

        {/* Access Level (read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Portal Access Level</label>
          <p className="text-sm text-gray-900 capitalize">
            {familyMember.portalAccessLevel.replace(/_/g, ' ').toLowerCase()}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Contact your care coordinator to change your access level
          </p>
        </div>

        {/* Edit Mode Actions */}
        {isEditing && (
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleSave}
              disabled={updateProfile.isPending}
              className="flex-1 rounded-md bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
            >
              {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={() => {
                setFormData({
                  firstName: familyMember.firstName,
                  lastName: familyMember.lastName,
                  email: familyMember.email,
                  phoneNumber: familyMember.phoneNumber,
                  preferredContactMethod: familyMember.preferredContactMethod,
                  relationship: familyMember.relationship,
                  relationshipNote: familyMember.relationshipNote || '',
                });
                setIsEditing(false);
              }}
              className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AccessTab: React.FC<{ familyMember: FamilyMember }> = ({ familyMember }) => {
  const { data: accessSettings, isLoading } = useFamilyAccessSettings(familyMember.id);
  const updateAccess = useUpdateFamilyAccessSettings(familyMember.id);
  const [localSettings, setLocalSettings] = useState<FamilyAccessSettings | null>(null);

  const settings = localSettings || accessSettings;

  const updateLocalSetting = <K extends keyof FamilyAccessSettings>(key: K, value: FamilyAccessSettings[K]) => {
    if (!settings) return;
    setLocalSettings({ ...settings, [key]: value });
  };

  const saveAccess = () => {
    if (!settings) return;
    updateAccess.mutate(settings, {
      onSuccess: () => setLocalSettings(null),
    });
  };

  if (isLoading || !settings) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600">
        Loading access settings...
      </div>
    );
  }

  const permissionRows: Array<{
    key: keyof FamilyAccessSettings;
    title: string;
    description: string;
  }> = [
    {
      key: 'showVisitUpdates',
      title: 'Visit updates',
      description: 'Completed visit status, visit history and family-safe visit notes.',
    },
    {
      key: 'showCarePlan',
      title: 'Care plan summary',
      description: 'Goals, routines, progress and planned support visible to family.',
    },
    {
      key: 'showHealthUpdates',
      title: 'Health updates',
      description: 'Family-safe health and wellbeing updates after visits.',
    },
    {
      key: 'showDocuments',
      title: 'Permitted documents',
      description: 'Only documents approved for family access are visible.',
    },
    {
      key: 'allowMessaging',
      title: 'Secure messaging',
      description: 'Family can send messages to the care team through the portal.',
    },
    {
      key: 'receiveVisitNotifications',
      title: 'Automatic visit notifications',
      description: 'Family receives notifications when visits are completed.',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-purple-100 bg-purple-50 p-6">
        <h2 className="text-lg font-semibold text-purple-950">Family access per client</h2>
        <p className="mt-2 text-sm leading-6 text-purple-900">
          This family account is linked only to client ID <strong>{settings.clientId}</strong>. The API filters dashboard,
          visits, messages, notifications and documents using this client link before anything is shown.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-base font-semibold text-gray-950">Visible information</h3>
        <div className="mt-4 divide-y divide-gray-100">
          {permissionRows.map((row) => (
            <label key={row.key} className="flex items-start justify-between gap-4 py-4">
              <span>
                <span className="block text-sm font-semibold text-gray-950">{row.title}</span>
                <span className="mt-1 block text-sm leading-6 text-gray-600">{row.description}</span>
              </span>
              <input
                type="checkbox"
                checked={Boolean(settings[row.key])}
                onChange={(event) => updateLocalSetting(row.key, event.target.checked as FamilyAccessSettings[typeof row.key])}
                className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </label>
          ))}
        </div>
        <button
          type="button"
          onClick={saveAccess}
          disabled={updateAccess.isPending}
          className="mt-5 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {updateAccess.isPending ? 'Saving...' : 'Save access settings'}
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-base font-semibold text-gray-950">Restricted information</h3>
        <p className="mt-2 text-sm leading-6 text-gray-600">
          These categories are blocked from the family portal even when all options are enabled:
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {(settings.restrictedData || []).map((item) => (
            <span key={item} className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Data & Privacy Tab
 */
const DataTab: React.FC<{ familyMember: FamilyMember }> = ({ familyMember }) => {
  const handleExportData = () => {
    alert('Data export functionality will download your data in JSON format');
    console.log('Exporting data for:', familyMember.id);
  };

  return (
    <div className="space-y-6">
      {/* Data Export */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Export Your Data</h2>
        <p className="text-sm text-gray-600 mb-4">
          Download all your portal data, including notification history, messages, and activity logs.
          Data will be provided in JSON format.
        </p>
        <button
          onClick={handleExportData}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
        >
          Export My Data
        </button>
      </div>

      {/* Privacy Information */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Privacy & Security</h2>
        <div className="space-y-4 text-sm">
          <div>
            <h3 className="font-medium text-gray-900 mb-1">Last Login</h3>
            <p className="text-gray-600">
              {familyMember.lastLoginAt
                ? new Date(familyMember.lastLoginAt).toLocaleString()
                : 'Never logged in'}
            </p>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-1">Portal Status</h3>
            <p className="text-gray-600 capitalize">{familyMember.status.toLowerCase()}</p>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-1">Access Granted</h3>
            <p className="text-gray-600">
              {new Date(familyMember.accessGrantedAt).toLocaleDateString()}
            </p>
          </div>

          {familyMember.accessExpiresAt && (
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Access Expires</h3>
              <p className="text-gray-600">
                {new Date(familyMember.accessExpiresAt).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Account Actions */}
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <h2 className="text-lg font-semibold text-red-900 mb-4">Deactivate Portal Access</h2>
        <p className="text-sm text-red-700 mb-4">
          If you no longer need portal access, you can request deactivation. This action requires
          confirmation from your care coordinator.
        </p>
        <button
          onClick={() =>
            alert('Portal deactivation requires coordinator approval. Please contact your care team.')
          }
          className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 transition-colors"
        >
          Request Deactivation
        </button>
      </div>
    </div>
  );
};
